import { useNavigate } from 'react-router';
import { ReactNode, useState, useContext } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  useMediaQuery,
  useTheme,
  Typography,
  Chip,
  ListItemAvatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROUTES, routeWithParams } from 'router';
import { EntityTypes, Tenant } from 'types';
import MobilePanelList from 'components/lists/MobilePanelList';
import {
  useMaterialReactTable,
  type MRT_ColumnDef,
  MaterialReactTable,
  type MRT_RowSelectionState,
} from 'material-react-table';
import { History } from '@mui/icons-material';
import { getFormattedAddress, ThemeContext } from 'utils';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';
import MRTableToolbar from 'components/tables/PanelTableToolbar';

const TenantSelect = (): ReactNode => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const { state, dispatch } = useContext(AppContext);
  const { setTenantBranding } = useContext(ThemeContext);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({
    [state.tenant?.id?.toString() ?? '']: true,
  });

  const handleTenantSelection = (tenant: Tenant) => {
    dispatch({
      type: ACTION_TYPE.SET_ACTIVE_TENANT,
      payload: tenant,
    });
    setTenantBranding({
      branding: tenant?.data?.branding,
      companyPath: tenant?.data?.companyPath,
    });
    setRowSelection({
      [tenant.id.toString()]: true,
    });
  };

  const handleViewRevisions = (id: number) => {
    navigate(
      routeWithParams(ROUTES.REVISIONS, [`${id}`, EntityTypes.Tenant, `${id}`]),
    );
  };

  const columns: MRT_ColumnDef<Tenant>[] = [
    {
      header: t('tenant.name', { ns: 'entities' }),
      accessorKey: 'name',
    },
    {
      header: t('tenant_group.name', { ns: 'entities' }),
      accessorKey: 'tenantGroup.name',
    },
    {
      header: t('tenant.publicContactInfo.location.label', { ns: 'entities' }),
      id: 'publicContactInfo',
      accessorFn: (row) => getFormattedAddress(row.publicContactInfo?.location),
      size: 250,
    },
    {
      header: t('property.label_plural', { ns: 'entities' }),
      id: 'propertyCount',
      accessorFn: (row) => row.data?.propertyCount,
    },
  ];

  const tableDefaults = useDefaultTableOptions<Tenant>(TableType.PANEL);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: state.userTenants,
    columns,
    enableRowSelection: true,
    enableColumnOrdering: false,
    enableColumnActions: false,
    enableMultiRowSelection: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'comfortable',
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading: false,
      isSaving: false,
      showLoadingOverlay: false,
      rowSelection,
    },
    getRowId: (row) => `${row.id}`,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      const selectedId = Object.keys(newSelection).find(
        (key) => newSelection[key],
      );
      if (selectedId) {
        const tenant = state.userTenants.find(
          (t) => t.id.toString() === selectedId,
        );
        if (tenant) handleTenantSelection(tenant);
      }
    },
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        isSubmitting={false}
        optionsDisabled={true}
        hideSearch={false}
        hideFullScreen={true}
        hideColumnFilters={true}
        hideHideShowColumns={true}
        hidden={false}
      />
    ),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'pointer',
        backgroundColor: 'white',
      },
      onClick: () => handleTenantSelection(row.original),
    }),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={0}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      return options;
    },
  });

  if (isMobile) {
    const itemHeight = 70;
    const renderItem = (item: Tenant): ReactNode => (
      <ListItem
        key={item.id}
        id={item.id.toString()}
        onClick={() => handleTenantSelection(item)}
        sx={{ height: itemHeight, backgroundColor: 'white' }}
      >
        <ListItemText
          primary={
            <Typography variant='subtitle1' noWrap>
              {item.name}
            </Typography>
          }
          secondary={
            <Typography variant='subtitle2' noWrap>
              {item.tenantGroup?.name || ''}
            </Typography>
          }
        />
        {item.id === state.tenant.id && (
          <ListItemAvatar>
            <Chip label={t('active', { ns: 'entities' })} color='primary' />
          </ListItemAvatar>
        )}
      </ListItem>
    );

    return (
      <MobilePanelList<Tenant>
        data={state.userTenants}
        withSearchBar
        itemHeight={itemHeight}
        columns={columns.map((c) => ({
          name: c.header,
          searchKey: c.accessorKey,
        }))}
        title={t('tenant.label_plural', { ns: 'entities' })}
        renderItem={renderItem}
      />
    );
  }

  return <MaterialReactTable table={table} />;
};

export default TenantSelect;
